// After a deploy, a tab still running the old index.html asks for the old release's hashed
// chunks, which the server no longer has (S65). The fix is a reload, which fetches the new
// index.html (served `no-cache`). Reload once per short window, so a chunk that is genuinely
// broken shows the error page instead of looping.

const KEY = "ocs:chunk-reload";
const WINDOW_MS = 30_000;

const CHUNK_ERRORS = [
  "Failed to fetch dynamically imported module", // Chromium
  "error loading dynamically imported module", // Firefox
  "Importing a module script failed", // Safari
  "is not a valid JavaScript MIME type", // an old server answering with index.html
  "Unable to preload CSS",
];

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  return CHUNK_ERRORS.some((m) => message.includes(m));
}

type Store = Pick<Storage, "getItem" | "setItem">;

function session(): Store | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Whether a reload is still allowed: none happened in the last window. */
export function canReload(store: Store | null = session(), now = Date.now()): boolean {
  try {
    return now - Number(store?.getItem(KEY) ?? 0) >= WINDOW_MS;
  } catch {
    return true;
  }
}

/** Reloads the page unless it already did so in the last window; true when it reloaded. */
export function reloadOnce(reload: () => void = () => window.location.reload(), store: Store | null = session(), now = Date.now()): boolean {
  if (!canReload(store, now)) return false;
  try {
    store?.setItem(KEY, String(now));
  } catch {
    // Storage unavailable: reload anyway; the window guard is a convenience.
  }
  reload();
  return true;
}

/** Vite raises `vite:preloadError` when a lazy chunk's preload fails: reload instead. */
export function installChunkReload(target: Window = window): void {
  target.addEventListener("vite:preloadError", (event) => {
    if (reloadOnce()) event.preventDefault();
  });
}
